import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useCreateCustomerPortalSessionMutation,
  useCreateOrRetrieveCheckoutSessionMutation,
  useEmailVerified,
  useFeatureFlag,
  useGroupUserRoles,
  useOnboardingStep,
  usePaymentStatus,
  useSubjectLanguageVariants,
  useSubscriptionDetails,
  useUserInfo,
  useUserState,
} from '@features/user';
import { useNavTutorialsLabelExperiment } from '@hooks/useNavTutorialsLabelExperiment';
import { useRecordingSession } from '@hooks/useRecordingSession';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  KeyboardArrowLeftOutlined as ArrowOutIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Alert,
  AppBar,
  Badge,
  Box,
  Button,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Modal,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { hasAdminPrivileges, hasBillingAccess } from '@utils/roleUtils';

import { useRecordingContext } from '../../context/RecordingContext';
import { useThemeContext } from '../../context/theme/context';
import { useUIState } from '../../context/uiState';
import { useModal } from '../../store/hooks';
import CustomButton from '../../styles/CustomButtons';
import { trackEvent } from '../../utils/analytics_utils';
import { unixTimestampToDaysRemaining } from '../../utils/utils';
import EmailVerificationPrompt from '../../Views/Shared/EmailVerificationPrompt';
import { AlertBanner } from '../AlertBanner';
import UserMenuButton from '../Buttons/UserMenuButton';
import { ConditionalTooltip } from '../ConditionalTooltip';
import { JotPalWordmarkLogo, JotPsychWordmarkLogo } from '../Icons/Logo';
import { MODAL_TYPES } from '../modals';
import NavSearch from '../NavSearch/NavSearch';
import { useGlobalTutorial } from '../Tutorial/GlobalTutorialContext';

import type { RouteItem } from './Nav.types';
import { NavBranding } from './NavBranding';
import { NavDropdownItem } from './NavDropdownItem';
import { NavHelpMenu } from './NavHelpMenu';

export const Nav = () => {
  // Migrate to new user feature hooks
  const paymentStatus = usePaymentStatus();
  const emailVerified = useEmailVerified();
  const userInfo = useUserInfo();
  const groupUserRoles = useGroupUserRoles();
  const encountersFeatureFlag = useFeatureFlag('encounters');
  const generatedFirstNote = useOnboardingStep('generated_first_note');
  const { pluralCap: subjectLanguagePluralCap } = useSubjectLanguageVariants();
  const { startRecording } = useRecordingSession();
  const tutorialsLabel = useNavTutorialsLabelExperiment();
  const billingAuditFeatureGate = useFeatureGate('feature_billing_audit');

  // Fetch subscription details from the new API
  const { data: subscriptionDetails } = useSubscriptionDetails();
  const [createOrRetrieveCheckoutSession] = useCreateOrRetrieveCheckoutSessionMutation();
  const [createCustomerPortalSession] = useCreateCustomerPortalSessionMutation();

  const userState = useUserState();

  const [loadingBilling, setLoadingBilling] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { navigation: navigationMetrics } = useEventTracking();
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState<boolean>(false);
  const { state: UIState, setState, showAlertBanner } = useUIState();
  const { logout } = useAuth0();
  const theme = useTheme();
  const { open } = useModal();
  const { brandLanguage } = useThemeContext();
  const { platform } = UIState;
  const { hasTutorials, startTutorial } = useGlobalTutorial();

  const { recordingStatus, elapsedTime, allowRecorderControl } = useRecordingContext();

  // Custom breakpoint for nav drawer at 1100px
  const isDesktop = useMediaQuery('(min-width: 1100px)');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileOpenMenuId, setMobileOpenMenuId] = useState<string | null>(null);
  const [hasOpenedDrawer, setHasOpenedDrawer] = useState(false);

  // Expand Documentation menu by default on first drawer open only
  useEffect(() => {
    if (drawerOpen && !hasOpenedDrawer) {
      setMobileOpenMenuId('DocumentationMenu');
      setHasOpenedDrawer(true);
    }
  }, [drawerOpen, hasOpenedDrawer]);

  const isLeftSideBarPage = ['/note/', '/test', '/templates', '/demo'].some(path =>
    location.pathname.startsWith(path),
  );
  const formatTime = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes)}:${seconds.toString().padStart(2, '0')}`;
  };

  const isRecorderPage =
    location.pathname.includes('/record') || location.pathname.includes('/encounters/'); // Check for both record and encounters paths

  // Calculate if trial is ending soon or subscription needs attention
  const needsBillingAttention = useMemo(() => {
    // Check if subscription is paused or canceled
    if (paymentStatus && ['paused', 'canceled'].includes(paymentStatus)) {
      return {
        shouldShow: true,
        message: 'Your subscription is inactive. Click to restore access to your account.',
      };
    }

    // Check if trial is ending soon
    if (subscriptionDetails?.status === 'trialing' && subscriptionDetails?.current_period_end) {
      const remainingSeconds =
        subscriptionDetails.current_period_end - Math.floor(Date.now() / 1000);
      if (remainingSeconds <= 86400) {
        // Less than 24 hours
        return {
          shouldShow: true,
          message: 'Your trial ends in less than 24 hours! Click to continue your subscription.',
        };
      } else {
        const daysRemaining = unixTimestampToDaysRemaining(subscriptionDetails.current_period_end);
        if (daysRemaining <= 3) {
          return {
            shouldShow: true,
            message: 'Your trial is ending soon. Click to extend your subscription.',
          };
        }
      }
    }

    return { shouldShow: false, message: '' };
  }, [paymentStatus, subscriptionDetails?.status, subscriptionDetails?.current_period_end]);

  const handleBillingClick = async () => {
    setLoadingBilling(true);

    try {
      let data;
      if (paymentStatus && ['canceled'].includes(paymentStatus)) {
        // Generate checkout session
        data = await createOrRetrieveCheckoutSession({}).unwrap();
      } else {
        // Generate billing portal link
        data = await createCustomerPortalSession({}).unwrap();
      }

      const url =
        'portal_session_url' in data
          ? data.portal_session_url
          : 'checkout_session_url' in data
            ? data.checkout_session_url
            : undefined;

      if (url) {
        window.open(url, '_blank');
      } else {
        // Fallback to default Stripe billing portal
        window.open(
          `https://billing.stripe.com/p/login/7sIdRo5NjdyN1QA8ww?prefilled_email=${encodeURIComponent(
            userInfo?.email || '',
          )}`,
          '_blank',
        );
      }
    } catch (error) {
      console.error('Error creating session:', error);
      // Fallback to default Stripe billing portal
      window.open(
        `https://billing.stripe.com/p/login/7sIdRo5NjdyN1QA8ww?prefilled_email=${encodeURIComponent(
          userInfo?.email || '',
        )}`,
        '_blank',
      );
    } finally {
      setLoadingBilling(false);
    }
  };

  const handleToggleSidebar = () => {
    if (isLeftSideBarPage) {
      setState({
        ...UIState,
        sidebar: { isHidden: !UIState.sidebar.isHidden },
      });
    } else {
      setDrawerOpen(!drawerOpen);
    }
  };

  const routeMap: RouteItem[] = [
    // 1. Schedule - direct link
    {
      path: '/schedule',
      pageName: 'Schedule',
      id: 'ScheduleView',
      testId: 'nav-schedule',
      condition: () => {
        return encountersFeatureFlag;
      },
    },
    {
      path: '/patients',
      pageName: subjectLanguagePluralCap,
      id: 'PatientProfiles',
      testId: 'nav-patient-profiles',
    },
    // 2. Patients - dropdown
    // {
    //   path: null,
    //   pageName: subjectLanguagePluralCap,
    //   id: 'PatientsMenu',
    //   testId: 'nav-patients-menu',
    //   isDropdown: true,
    //   children: [
    //     {
    //       path: '/patients',
    //       pageName: 'Profiles',
    //       id: 'PatientProfiles',
    //       testId: 'nav-patient-profiles',
    //     },
    //     {
    //       path: '/communications',
    //       pageName: 'Communications',
    //       id: 'PatientCommunications',
    //       testId: 'nav-patient-communications',
    //     },
    //   ],
    // },
    // 3. Documentation - dropdown
    {
      path: null,
      pageName: 'Documentation',
      id: 'DocumentationMenu',
      testId: 'nav-documentation-menu',
      isDropdown: true,
      children: [
        { path: '/notes', pageName: 'Notes', id: 'NotesView', testId: 'nav-notes' },
        {
          path: '/templates',
          pageName: 'Note Templates',
          id: 'TemplatesView',
          testId: 'nav-templates',
        },
        { path: '/labs', pageName: 'Labs', id: 'LabsView', testId: 'nav-labs' },
        { path: '/form-documents', pageName: 'Forms', id: 'FormsView', testId: 'nav-forms' },
        {
          path: '/documents',
          pageName: 'Other Docs',
          id: 'DocumentsView',
          testId: 'nav-documents',
        },
      ],
    },
    // 4. Billing + Auditing - dropdown
    {
      path: null,
      pageName: 'Billing + Auditing',
      id: 'BillingAuditingMenu',
      testId: 'nav-billing-auditing-menu',
      isDropdown: true,
      children: [
        {
          path: '/jotaudit',
          pageName: 'JotAudit',
          id: 'AuditView',
          testId: 'nav-audit',
        },
        {
          path: '/jotcoach',
          pageName: 'JotCoach',
          id: 'CoachView',
          testId: 'nav-coach',
        },
        {
          path: '/bill-insurance',
          pageName: 'Bill Insurance',
          id: 'BillInsurance',
          testId: 'nav-bill-insurance',
        },
        {
          path: '/bill-patients',
          pageName: `Bill ${subjectLanguagePluralCap}`,
          id: 'BillPatients',
          testId: 'nav-bill-patients',
        },
      ],
      condition: () => billingAuditFeatureGate,
    },
    // 5. Your Practice - dropdown
    {
      path: null,
      pageName: 'Your Practice',
      id: 'YourPracticeMenu',
      testId: 'nav-your-practice-menu',
      isDropdown: true,
      children: [
        {
          path: '/practice/clinicians',
          pageName: 'Clinicians',
          id: 'CliniciansView',
          testId: 'nav-clinicians',
          condition: () => hasBillingAccess(groupUserRoles),
        },
        {
          path: '/practice/subscriptions',
          pageName: 'Subscriptions',
          id: 'SubscriptionsView',
          testId: 'nav-subscriptions',
          condition: () => hasBillingAccess(groupUserRoles),
        },
        {
          path: '/practice/settings',
          pageName: 'Settings',
          id: 'PracticeSettingsView',
          testId: 'nav-practice-settings',
          condition: () => hasBillingAccess(groupUserRoles),
        },
        // TODO: Re-enable when Reports and Analytics is ready
        // {
        //   path: '/reports-analytics',
        //   pageName: 'Reports and Analytics',
        //   id: 'ReportsAnalytics',
        //   testId: 'nav-reports-analytics',
        // },
      ],
      condition: () => {
        return hasAdminPrivileges(groupUserRoles) || hasBillingAccess(groupUserRoles);
      },
    },
    // 6. Record - action button
    {
      path: undefined, // Remove path to prevent automatic navigation
      pageName: 'Record',
      id: 'RecorderView',
      testId: 'nav-record',
      customStyle: { color: theme.palette.error.main },
      onClick: () => {
        startRecording();
      },
    },
  ];

  const getButtonStyle = (route: (typeof routeMap)[number]) => {
    // Check if it's a dropdown menu (has children but no path)
    if (!route.path && route.children) {
      // Type guard - only RouteItem can have children
      const routeItem = route;
      const isChildActive = routeItem.children?.some(
        child =>
          child.path !== null &&
          (location.pathname === child.path || location.pathname.startsWith(child.path)),
      );
      return {
        fontWeight: isChildActive ? 'bold' : 'normal',
        color: isChildActive ? theme.palette.primary.light : 'textColors.lightHeader',
      };
    }

    // External links should never show as active
    const routeItem = route;
    if (routeItem.externalUrl) {
      const baseStyle = {
        fontWeight: 'normal',
        color: 'textColors.lightHeader' as const,
      };
      if (routeItem.customStyle) {
        return { ...baseStyle, ...routeItem.customStyle };
      }
      return baseStyle;
    }

    const isActive =
      route.path &&
      (location.pathname === route.path ||
        (route.path === '/encounters/active' && location.pathname.startsWith('/encounters/')) ||
        (location.pathname.startsWith(route.path) && route.path !== '/'));
    const baseStyle = {
      fontWeight: isActive ? 'bold' : 'normal',
      color: isActive ? theme.palette.primary.light : 'textColors.lightHeader',
    };

    // Only RouteItem can have customStyle
    if (routeItem.customStyle) {
      return { ...baseStyle, ...routeItem.customStyle };
    }

    return baseStyle;
  };

  useEffect(() => {
    // Calculate days since first login
    const daysSinceFirstLogin = () => {
      if (!userState?.firstLoginTime) return 0;
      // Convert seconds to milliseconds if needed (Python timestamps are in seconds)
      const timestamp =
        userState.firstLoginTime < 10000000000
          ? userState.firstLoginTime * 1000
          : userState.firstLoginTime;
      const firstLogin = new Date(timestamp);
      const now = new Date();
      const timeDiff = now.getTime() - firstLogin.getTime();
      return Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    };

    // Check if we should show the onboarding help banner
    const hasNotGeneratedFirstNote = !generatedFirstNote;
    const hasBeenMoreThanADay = daysSinceFirstLogin() >= 1;
    const bannerNotShown = !localStorage.getItem('onboardingBannerShown');

    if (hasNotGeneratedFirstNote && hasBeenMoreThanADay && bannerNotShown) {
      // Small delay for better UX (5 seconds)
      const timerId = setTimeout(() => {
        localStorage.setItem('onboardingBannerShown', 'true');
        showAlertBanner(
          'Need help getting started? Click here to schedule 15 minutes with our product team.',
          'warning',
          false,
          () => {
            window.open(
              `https://calendly.com/d/cqyn-mhr-yw5/jotpsych-onboarding-support`,
              '_blank',
            );
          },
        );
      }, 5000); // Reduced to 5 seconds

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [userState?.firstLoginTime, generatedFirstNote, showAlertBanner]);

  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Add state to track if search has text
  const [hasSearchText, setHasSearchText] = useState(false);

  // Function to handle hover start for search icon
  const handleHoverStart = () => {
    setIsHovering(true);

    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Set new timer to open toolbar after 150ms
    hoverTimerRef.current = setTimeout(() => {
      setToolbarOpen(true);
    }, 150);
  };

  // Function to handle hover end for search icon
  const handleHoverEnd = () => {
    setIsHovering(false);

    // Clear the timer if the mouse leaves before 150ms
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  // Function to handle search text changes
  const handleSearchTextChange = (hasText: boolean) => {
    setHasSearchText(hasText);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  // Add useEffect to handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Command+P (Mac) or Ctrl+P (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'p') {
        // Prevent default browser behavior (print dialog)
        event.preventDefault();

        // Always toggle the toolbar (open if closed, close if open)
        setToolbarOpen(prevState => !prevState);

        // Clear any hover timer that might be active
        if (hoverTimerRef.current) {
          clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        color="primary"
        sx={{
          bgcolor: 'grey.100',
        }}
        elevation={1}
      >
        <Toolbar
          sx={{
            minHeight: 'inherit',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'space-between',
              flex: 1,
            }}
          >
            <Box
              color="primary"
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isLeftSideBarPage ? (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="back"
                  onClick={() => {
                    navigate(-1);
                  }}
                  sx={{ display: isDesktop ? 'none' : 'flex', color: 'black' }}
                >
                  <ArrowOutIcon />
                </IconButton>
              ) : (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleToggleSidebar}
                  sx={{ display: isDesktop ? 'none' : 'flex', color: 'black' }}
                >
                  <MenuIcon />
                </IconButton>
              )}

              {/* Search icon that triggers toolbar */}
              <Tooltip
                title={`${navigator.userAgent.indexOf('Mac') !== -1 ? '⌘' : 'Ctrl'} + P`}
                placement="left"
                arrow
              >
                <IconButton
                  color="inherit"
                  aria-label="search"
                  onClick={() => {
                    setToolbarOpen(true);
                  }}
                  onMouseEnter={handleHoverStart}
                  onMouseLeave={handleHoverEnd}
                  sx={{
                    color: toolbarOpen ? theme.palette.primary.main : theme.palette.grey[700],
                    mr: 1,
                    transition: 'all 0.2s ease',
                    transform: isHovering ? 'scale(1.2)' : 'scale(1)',
                    animation: isHovering ? 'searchPulse 1.2s ease infinite' : 'none',
                    '@keyframes searchPulse': {
                      '0%': { opacity: 0.7 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0.7 },
                    },
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>

              <NavBranding />
              {/* {isLeftSideBarPage && (
                <CustomButton
                  sx={{ display: { xs: "inherit", md: "none" } }}
                  onClick={() => {
                    setState((prevState: UIStateType) => ({
                      ...prevState,
                      navigation: { currentPage: "home" },
                    }));
                    navigate("/");
                  }}
                  size="large"
                  aria-label="logo-home-button"
                >
                  Home
                </CustomButton>
              )} */}

              <Box
                className="left-nav-buttons-container"
                sx={{ display: isDesktop ? 'flex' : 'none' }}
              >
                {routeMap
                  .filter(route => !route.condition || route.condition())
                  .map(route => {
                    // Handle dropdown menus (Customization and Org)
                    if (route.isDropdown) {
                      return (
                        <NavDropdownItem
                          key={route.id}
                          route={route}
                          onNavigate={(path, pageName) => {
                            navigationMetrics.clicked({
                              menu_item: pageName.toLowerCase().replace(/\s+/g, '_'),
                              source_path: location.pathname,
                              destination_path: path,
                              is_dropdown: true,
                            });
                          }}
                        />
                      );
                    }

                    // Regular menu items
                    // Determine component type and props based on route type
                    const isExternalLink = Boolean(route.externalUrl);
                    const isInternalLink = Boolean(route.path);

                    // Build component props conditionally
                    let componentProps: Record<string, unknown> = {};
                    if (isExternalLink) {
                      componentProps = {
                        component: 'a',
                        href: route.externalUrl,
                        ...(route.externalLinkProps || {}),
                      };
                    } else if (isInternalLink && route.path) {
                      componentProps = {
                        component: Link,
                        to: route.path,
                      };
                    }

                    return (
                      <CustomButton
                        key={route.path || route.externalUrl || route.id}
                        data-testid={route.testId}
                        sx={getButtonStyle(route)}
                        variant="text"
                        {...componentProps}
                        onClick={() => {
                          route.onClick?.();
                          if (route.path) {
                            // Track navigation
                            navigationMetrics.clicked({
                              menu_item: route.pageName.toLowerCase().replace(/\s+/g, '_'),
                              source_path: location.pathname,
                              destination_path: route.path,
                              is_dropdown: false,
                            });
                          }
                          if (route.externalUrl) {
                            // Track external link clicks
                            navigationMetrics.clicked({
                              menu_item: route.pageName.toLowerCase().replace(/\s+/g, '_'),
                              source_path: location.pathname,
                              destination_path: route.externalUrl,
                              is_dropdown: false,
                            });
                          }
                        }}
                      >
                        {route.badge ? (
                          <Badge
                            color="info"
                            badgeContent="NEW"
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            sx={{
                              '& .MuiBadge-badge': {
                                fontSize: '0.5rem',
                                padding: '0 6px',
                                fontWeight: 'bold',
                                transform: 'scale(1) translate(30%, -80%)',
                              },
                            }}
                          >
                            {route.pageName}
                          </Badge>
                        ) : (
                          route.pageName
                        )}
                      </CustomButton>
                    );
                  })}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { md: 0, lg: 1, xl: 2 },
            }}
          >
            <Stack
              direction={'row'}
              gap={{ md: 2, lg: 3, xl: 4 }}
              sx={{ display: isDesktop ? 'flex' : 'none', alignItems: 'center' }}
            >
              {/* Desktop-only items (lg+): Next Appointment Badge, Recording Status, Email Verification */}
              <Box
                sx={{
                  display: { xs: 'none', lg: 'flex' },
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {/* Recording Status - Redesigned to match NextAppointmentBadge */}
                {!isRecorderPage && recordingStatus.isRecording && allowRecorderControl && (
                  <Box
                    onClick={() => {
                      // Track quick action navigation
                      navigationMetrics.quickAction({
                        action_type: 'record',
                        source_path: location.pathname,
                      });
                      startRecording();
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'error.main',
                      color: 'white',
                      borderRadius: '6px',
                      p: '4px 8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: 1,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          animation: 'pulse 1s infinite',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 'medium',
                        }}
                      >
                        {formatTime(elapsedTime)}
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {/* Email Verification */}
                {paymentStatus === 'active' && !emailVerified && (
                  <ConditionalTooltip
                    condition={true}
                    title="Click here to verify your email."
                    placement="bottom"
                    arrow
                  >
                    <Alert
                      severity="error"
                      sx={{
                        py: 0,
                        px: 1,
                        cursor: 'pointer',
                        height: 'auto',
                        borderRadius: '100px',
                        border: '1px solid red',
                        '& .MuiAlert-message': {
                          width: '100%',
                        },
                      }}
                      onClick={() => {
                        setShowEmailVerificationModal(true);
                      }}
                    >
                      Email not verified.
                    </Alert>
                  </ConditionalTooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {/* Tutorials Button */}
                <CustomButton
                  variant="text"
                  component="a"
                  href="https://jotpsych.notion.site/How-to-Use-JotPsych-2a08b51156598037b2f0e2457e372414"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<VideocamIcon sx={{ fontSize: '26px !important' }} />}
                  onClick={() => {
                    // Track tutorial link clicks
                    navigationMetrics.clicked({
                      menu_item: 'tutorials',
                      source_path: location.pathname,
                      destination_path: 'external',
                      is_dropdown: false,
                    });
                  }}
                >
                  {tutorialsLabel}
                </CustomButton>
                {/* Help Menu */}
                <NavHelpMenu />
              </Box>
            </Stack>

            {/* Trial Warning Icon - shows when trial is ending soon or for paused/canceled subscriptions */}
            {needsBillingAttention.shouldShow && (
              <Tooltip title={needsBillingAttention.message} placement="bottom" arrow>
                <IconButton
                  onClick={handleBillingClick}
                  disabled={loadingBilling}
                  sx={{
                    color: theme.palette.warning.main,
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.7 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0.7 },
                    },
                    mx: 1,
                  }}
                  size="small"
                >
                  <WarningIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* User Menu Button */}
            <UserMenuButton />
          </Box>
        </Toolbar>

        {/* Fixed position container for toolbar - prevents layout shifts */}
        <Box
          sx={{
            position: 'fixed',
            top: {
              xs: '52px',
              sm: '64px',
            },
            left: 0,
            right: 0,
            zIndex: 100,
            pointerEvents: toolbarOpen ? 'auto' : 'none',
          }}
        >
          {/* Expandable toolbar */}
          <Box
            sx={{
              height: toolbarOpen ? '60px' : '0px',
              overflow: 'hidden',
              transition: 'height 0.3s ease, box-shadow 0.3s ease',
              backgroundColor: theme.palette.grey[100],
              boxShadow: toolbarOpen ? '0px 4px 10px rgba(0, 0, 0, 0.1)' : 'none',
              borderTop: toolbarOpen ? `1px solid ${theme.palette.grey[300]}` : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              padding: '0 24px', // Keep padding consistent
              paddingTop: toolbarOpen ? undefined : 0,
              paddingBottom: toolbarOpen ? undefined : 0,
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                opacity: toolbarOpen ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
              onMouseLeave={() => {
                // Only close the toolbar if there's no search text
                if (!hasSearchText) {
                  setToolbarOpen(false);
                }
              }}
            >
              <NavSearch
                onClose={() => {
                  setToolbarOpen(false);
                }}
                toolbarOpen={toolbarOpen}
                onSearchTextChange={handleSearchTextChange}
                setIsHovering={setIsHovering}
              />
            </Box>
          </Box>
        </Box>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
          }}
          sx={{
            display: isDesktop ? 'none' : 'block',
            '& .MuiDrawer-paper': {
              minWidth: '250px',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {/* Drawer Header with Logo and Close Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box
              onClick={() => {
                navigationMetrics.clicked({
                  menu_item: 'logo',
                  source_path: location.pathname,
                  destination_path: '/',
                  is_dropdown: false,
                });
                navigate('/');
                setDrawerOpen(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                minWidth: '120px',
              }}
            >
              {brandLanguage.brandName === 'JotPsych' && <JotPsychWordmarkLogo size={100} />}
              {brandLanguage.brandName === 'JotPal' && <JotPalWordmarkLogo size={100} />}
            </Box>
            <IconButton
              onClick={() => {
                setDrawerOpen(false);
              }}
              aria-label="close drawer"
              sx={{ color: 'textColors.lightHeader' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer Content - Scrollable List */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <List>
              {routeMap
                .filter(route => !route.condition || route.condition())
                .map(route => {
                  // Handle dropdown menus in mobile drawer
                  if (route.isDropdown) {
                    const isOpen = mobileOpenMenuId === route.id;
                    const toggleOpen = () => {
                      setMobileOpenMenuId(isOpen ? null : route.id);
                    };

                    return (
                      <React.Fragment key={route.id}>
                        <ListItem
                          component={ListItemButton}
                          onClick={toggleOpen}
                          sx={getButtonStyle(route)}
                        >
                          <ListItemText primary={route.pageName} />
                          {isOpen ? (
                            <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </ListItem>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {route.children
                              ?.filter(child => !child.condition || child.condition())
                              .map(child => {
                                const isPlaceholder = child.path === null;
                                if (isPlaceholder) {
                                  return (
                                    <ListItem key={child.id} disablePadding>
                                      <ListItemButton
                                        disabled
                                        sx={{
                                          pl: 4,
                                          ...getButtonStyle({ ...child, customStyle: undefined }),
                                          color: theme.palette.text.disabled,
                                          cursor: 'not-allowed',
                                        }}
                                      >
                                        <ListItemText
                                          primary={
                                            <>
                                              {child.pageName}
                                              <span
                                                style={{
                                                  marginLeft: '8px',
                                                  fontSize: '0.75rem',
                                                  opacity: 0.7,
                                                }}
                                              >
                                                (Coming soon)
                                              </span>
                                            </>
                                          }
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  );
                                }
                                // TypeScript type narrowing - we know path is not null here
                                if (!child.path) return null;
                                const childPath: string = child.path;
                                return (
                                  <ListItem
                                    key={child.id}
                                    component={Link}
                                    to={childPath}
                                    onClick={() => {
                                      // Track mobile dropdown navigation
                                      navigationMetrics.clicked({
                                        menu_item: child.pageName
                                          .toLowerCase()
                                          .replace(/\s+/g, '_'),
                                        source_path: location.pathname,
                                        destination_path: childPath,
                                        is_dropdown: true,
                                      });
                                      setDrawerOpen(false);
                                    }}
                                    sx={{
                                      pl: 4,
                                      ...getButtonStyle({ ...child, customStyle: undefined }),
                                    }}
                                  >
                                    <ListItemText primary={child.pageName} />
                                  </ListItem>
                                );
                              })}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  }

                  // Regular menu items in drawer
                  // Determine component type and props based on route type
                  const isMobileExternalLink = Boolean(route.externalUrl);
                  const isMobileInternalLink = Boolean(route.path);

                  // Build component props conditionally
                  let mobileComponentProps: Record<string, unknown> = {
                    component: ListItemButton,
                  };
                  if (isMobileExternalLink) {
                    mobileComponentProps = {
                      component: 'a',
                      href: route.externalUrl,
                      ...(route.externalLinkProps || {}),
                    };
                  } else if (isMobileInternalLink && route.path) {
                    mobileComponentProps = {
                      component: Link,
                      to: route.path,
                    };
                  }

                  return (
                    <ListItem
                      key={route.path || route.externalUrl || route.id}
                      {...mobileComponentProps}
                      data-testid={route.testId}
                      onClick={() => {
                        route.onClick?.();
                        if (route.path) {
                          // Track mobile navigation
                          navigationMetrics.clicked({
                            menu_item: route.pageName.toLowerCase().replace(/\s+/g, '_'),
                            source_path: location.pathname,
                            destination_path: route.path,
                            is_dropdown: false,
                          });
                        }
                        if (route.externalUrl) {
                          // Track external link clicks
                          navigationMetrics.clicked({
                            menu_item: route.pageName.toLowerCase().replace(/\s+/g, '_'),
                            source_path: location.pathname,
                            destination_path: route.externalUrl,
                            is_dropdown: false,
                          });
                        }
                        setDrawerOpen(false);
                      }}
                      sx={getButtonStyle(route)}
                    >
                      <ListItemText
                        primary={
                          route.badge ? (
                            <Badge
                              color="error"
                              badgeContent="NEW"
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  minWidth: '35px',
                                  padding: '0 6px',
                                  fontWeight: 'bold',
                                  transform: 'scale(1) translate(50%, 40%)',
                                },
                              }}
                            >
                              {route.pageName}
                            </Badge>
                          ) : (
                            route.pageName
                          )
                        }
                      />
                    </ListItem>
                  );
                })}
              <ListItem
                component="a"
                href="https://jotpsych.notion.site/How-to-Use-JotPsych-2a08b51156598037b2f0e2457e372414"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  // Track tutorial link clicks from mobile drawer
                  navigationMetrics.clicked({
                    menu_item: 'tutorials',
                    source_path: location.pathname,
                    destination_path: 'external',
                    is_dropdown: false,
                  });
                  setDrawerOpen(false);
                }}
                sx={{ fontWeight: 'normal', color: 'textColors.lightHeader' }}
              >
                <ListItemText primary={tutorialsLabel} />
              </ListItem>
              {/* Help Menu Section - Collapsible */}
              <ListItem
                component={ListItemButton}
                onClick={() => {
                  setMobileOpenMenuId(mobileOpenMenuId === 'HelpMenu' ? null : 'HelpMenu');
                }}
                sx={{ fontWeight: 'normal', color: 'textColors.lightHeader' }}
              >
                <HelpIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                <ListItemText primary="Help" />
                {mobileOpenMenuId === 'HelpMenu' ? (
                  <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
                ) : (
                  <ExpandMoreIcon />
                )}
              </ListItem>
              <Collapse in={mobileOpenMenuId === 'HelpMenu'} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {hasTutorials && (
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          startTutorial();
                          // Track help menu navigation
                          navigationMetrics.clicked({
                            menu_item: 'help_tutorial',
                            source_path: location.pathname,
                            destination_path: 'modal',
                            is_dropdown: true,
                          });
                          // Keep legacy tracking for now
                          trackEvent({
                            event: 'help_tutorial_clicked',
                            platform: platform,
                          });
                          setDrawerOpen(false);
                        }}
                        sx={{ pl: 4, fontWeight: 'normal', color: 'textColors.lightHeader' }}
                      >
                        <ListItemText primary="Show Page Tutorial" />
                      </ListItemButton>
                    </ListItem>
                  )}
                  <ListItem
                    component="a"
                    href="https://jotpsych.frontkb.com/en"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track help menu navigation
                      navigationMetrics.clicked({
                        menu_item: 'help_knowledgebase',
                        source_path: location.pathname,
                        destination_path: 'external',
                        is_dropdown: true,
                      });
                      // Keep legacy tracking for now
                      trackEvent({
                        event: 'help_knowledgebase_clicked',
                        platform: platform,
                      });
                      setDrawerOpen(false);
                    }}
                    sx={{
                      pl: 4,
                      fontWeight: 'normal',
                      color: 'textColors.lightHeader',
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemText primary="Help Articles" />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        // Track help menu navigation
                        navigationMetrics.clicked({
                          menu_item: 'help_consent_information',
                          source_path: location.pathname,
                          destination_path: 'modal',
                          is_dropdown: true,
                        });
                        // Keep legacy tracking for now
                        trackEvent({
                          event: 'help_consent_information_clicked',
                          platform: platform,
                        });
                        // Open consent modal
                        open(MODAL_TYPES.CONSENT_INFORMATION, {});
                        setDrawerOpen(false);
                      }}
                      sx={{ pl: 4, fontWeight: 'normal', color: 'textColors.lightHeader' }}
                    >
                      <ListItemText primary="Patient Consent & Privacy" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem
                    component="a"
                    href={`https://${brandLanguage.brandSite}/onboarding-support`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track help menu navigation
                      navigationMetrics.clicked({
                        menu_item: 'help_onboarding',
                        source_path: location.pathname,
                        destination_path: 'external',
                        is_dropdown: true,
                      });
                      // Keep legacy tracking for now
                      trackEvent({
                        event: 'help_onboarding_clicked',
                        platform: platform,
                      });
                      setDrawerOpen(false);
                    }}
                    sx={{
                      pl: 4,
                      fontWeight: 'normal',
                      color: 'textColors.lightHeader',
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemText primary="Book Onboarding" />
                  </ListItem>
                  <ListItem
                    component="a"
                    href={`https://${brandLanguage.brandSite}/contact`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track help menu navigation
                      navigationMetrics.clicked({
                        menu_item: 'help_contact',
                        source_path: location.pathname,
                        destination_path: 'external',
                        is_dropdown: true,
                      });
                      // Keep legacy tracking for now
                      trackEvent({
                        event: 'help_contactus_clicked',
                        platform: platform,
                      });
                      setDrawerOpen(false);
                    }}
                    sx={{
                      pl: 4,
                      fontWeight: 'normal',
                      color: 'textColors.lightHeader',
                      textDecoration: 'none',
                    }}
                  >
                    <ListItemText primary="Contact Support" />
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => {
                        // Track help menu navigation
                        navigationMetrics.clicked({
                          menu_item: 'help_diagnostics',
                          source_path: location.pathname,
                          destination_path: '/diagnostics',
                          is_dropdown: true,
                        });
                        // Keep legacy tracking for now
                        trackEvent({
                          event: 'help_diagnostics_clicked',
                          platform: platform,
                        });
                        navigate('/diagnostics');
                        setDrawerOpen(false);
                      }}
                      sx={{ pl: 4, fontWeight: 'normal', color: 'textColors.lightHeader' }}
                    >
                      <ListItemText primary="Troubleshooting Tools" />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </List>
          </Box>

          {/* Drawer Footer with Logout Button */}
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('userInfo');
                void logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
                setDrawerOpen(false);
              }}
              sx={{
                fontWeight: 'normal',
                color: 'textColors.lightHeader',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        </Drawer>
        {UIState.navbar.showCopyNotification && (
          <div
            className={`tooltip-animation absolute z-40 top-0 left-0 inline-block p-4 mt-16 w-full font-medium text-black duration-300 transition-opacity bg-green-400 shadow-sm tooltip`}
          >
            Copied
          </div>
        )}
        <AlertBanner />
      </AppBar>
      {/* Email Verification Prompt */}
      <Modal
        open={showEmailVerificationModal}
        onClose={() => {
          setShowEmailVerificationModal(false);
        }}
      >
        <Box>
          <EmailVerificationPrompt
            onClose={() => {
              setShowEmailVerificationModal(false);
            }}
          />
        </Box>
      </Modal>
    </>
  );
};
